import { Injectable } from '@angular/core';
import {Page} from "./api.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {environment} from "../../environments/environment";
import {SimpleUser, User} from "./user.service";
import {Attendee} from "./attendee.service";
import {ProfileRequirements} from "./profile-requirements.service";

export type RoomStatus = 'PLANNED' | 'ACTIVE' | 'COMPLETED' | 'DELETED' ;
export type RoomTemplateStatus = 'ACTIVE' | 'DELETED' ;

export type AccessLevels = { [key: string]: 'Owner' | 'Editor' | 'Viewer'; } ;

export class Room {

  id?: string;
  subjectId?: string ;
  code?: string;
  status?: RoomStatus ;
  name: string;
  description: string ;
  sandbox?: boolean ;
  createdBy?: SimpleUser ;
  createdAt?: Date ;
  runBy?: SimpleUser ;
  startedAt?: Date ;
  agenda: AgendaItem[] ;

  public constructor(name: string, description: string) {
    this.name = name ;
    this.description = description ;
    this.agenda = [] ;
  }
}

export class RoomQuery {
  name?: string ;
  before?: Date ;
  after?: Date ;
  createdById?: string ;
  runById?: string ;

  excludeSandbox: boolean ;

  public constructor() {
    this.excludeSandbox = false ;
  }
}

export class RoomTemplate {
  id?: string;
  subjectId?: string;

  name: string;
  description: string ;
  agenda: AgendaItem[] ;

  status: RoomTemplateStatus ;
  createdBy?: SimpleUser ;
  createdAt?: Date ;

  public constructor(name: string, description: string) {
    this.status= 'ACTIVE';
    this.name = name;
    this.description = description ;
    this.agenda = [] ;
  }
}

export type AgendaItemType = 'QUIZ' | 'CLASS_TASK' | 'VOLUNTEER_TASK' | 'OTHER' ;
export type AgendaItemStatus = 'PENDING' | 'ACTIVE' | 'SKIPPED' | 'COMPLETED' ;

export class AgendaItem {
  type: AgendaItemType ;
  forGroups: boolean ;
  title: string ;
  markup?: string ;
  duration?: number ;
  startedAt?: Date ;
  endedAt?: Date ;
  status: AgendaItemStatus ;

  constructor(type: AgendaItemType, forGroups: boolean, title: string) {
    this.type=type;
    this.forGroups = forGroups ;
    this.title = title ;
    this.status = "PENDING" ;
  }
}

export class OtherAgendaItem extends AgendaItem {

  constructor(title: string) {
    super('OTHER', false, title) ;
  }
}

export type RoomActivityType = 'QUIZ' | 'CLASS_TASK' | 'VOLUNTEER_TASK'
export type RoomActivityStatus = 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'PAUSED' | 'DELETED' ;
export class RoomActivity {
  id?: string ;
  type?: RoomActivityType ;
  status?: RoomActivityStatus ;
  roomId?: string ;
  forGroups: boolean ;
  createdAt?: Date ;

  constructor(forGroups:boolean) {
    this.forGroups = forGroups ;
  }
}



@Injectable({
  providedIn: 'root'
})
export class RoomService {

  constructor(private http: HttpClient) {

  }

  getRooms(subjectId: string, query: RoomQuery, page:number): Observable<Page<Room>> {

    let params = {
      q: JSON.stringify(query),
      page: (page) ? page : 0
    }

    return this.http.get(
      `${environment.apiHost}/subjects/${subjectId}/rooms`,
      {params}
    ) as Observable<Page<Room>>;
  }

  getRoom(id: string): Observable<Room> {
    return this.http.get(
      `${environment.apiHost}/rooms/${id}`
    ) as Observable<Room>;
  }

  getRoomActivities(roomId: string): Observable<RoomActivity[]> {
    return this.http.get<RoomActivity[]>(
      `${environment.apiHost}/rooms/${roomId}/activities`
    )
  }

  createRoom(subjectId: string, room:Room): Observable<Room> {
    return this.http.post(
      `${environment.apiHost}/subjects/${subjectId}/rooms`,
      room
    ) as Observable<Room>;
  }

  createRoomFromTemplate(subjectId: string, template:RoomTemplate): Observable<Room> {

    const room = new Room(template.name, template.description) ;
    room.agenda = template.agenda ;

    return this.http.post(
      `${environment.apiHost}/subjects/${subjectId}/rooms`,
      room
    ) as Observable<Room>;
  }

  createPracticeRoomFromTemplate(subjectId: string, template:RoomTemplate): Observable<Room> {

    const room = new Room(template.name + " (practice)", template.description) ;
    room.agenda = template.agenda ;
    room.sandbox = true ;

    return this.http.post(
      `${environment.apiHost}/subjects/${subjectId}/rooms`,
      room
    ) as Observable<Room>;
  }



  updateRoom(room:Room): Observable<Room> {
    return this.http.post(
      `${environment.apiHost}/rooms/${room.id}`,
      room
    ) as Observable<Room>;
  }

  joinRoom(roomId: string): Observable<Attendee> {
    return this.http.post(
      `${environment.apiHost}/rooms/${roomId}/attendees`,
      {}
    ) as Observable<Attendee>;
  }

  deleteRoom(roomId: string): Observable<Room> {
    return this.http.delete<Room>(
      `${environment.apiHost}/rooms/${roomId}`
    );
  }

  openRoom(roomId: string): Observable<Room> {
    return this.http.post<Room>(
      `${environment.apiHost}/rooms/${roomId}/actions`,
      {type: 'OPEN'}
    )
  }

  closeRoom(roomId: string): Observable<Room> {
    return this.http.post<Room>(
      `${environment.apiHost}/rooms/${roomId}/actions`,
      {type: 'CLOSE'}
    )
  }

  startNextAgendaItem(roomId: string): Observable<Room> {
    return this.http.post<Room>(
      `${environment.apiHost}/rooms/${roomId}/actions`,
      {type: 'START_AGENDA_ITEM'}
    )
  }

  skipNextAgendaItem(roomId: string): Observable<Room> {
    return this.http.post<Room>(
      `${environment.apiHost}/rooms/${roomId}/actions`,
      {type: 'SKIP_AGENDA_ITEM'}
    )
  }

  closeCurrentAgendaItem(roomId: string): Observable<Room> {
    return this.http.post<Room>(
      `${environment.apiHost}/rooms/${roomId}/actions`,
      {type: 'CLOSE_AGENDA_ITEM'}
    )
  }

  resetAgenda(roomId: string): Observable<Room> {
    return this.http.post<Room>(
      `${environment.apiHost}/rooms/${roomId}/actions`,
      {type: 'RESET_AGENDA'}
    )
  }

  resetScores(roomId: string): Observable<Room> {
    return this.http.post<Room>(
      `${environment.apiHost}/rooms/${roomId}/actions`,
      {type: 'RESET_SCORES'}
    )
  }




  getTemplates(subjectId:string, query:RoomQuery, page:number): Observable<Page<RoomTemplate>> {

    let params = {
      q: JSON.stringify(query),
      page: (page) ? page : 0
    }

    return this.http.get(
      `${environment.apiHost}/subjects/${subjectId}/room-templates`,
      {params}
    ) as Observable<Page<RoomTemplate>>;
  }

  getTemplate(id: string): Observable<RoomTemplate> {
    return this.http.get(
      `${environment.apiHost}/room-templates/${id}`
    ) as Observable<RoomTemplate>;
  }

  createTemplate(subjectId:string, template:RoomTemplate): Observable<RoomTemplate> {
    return this.http.post(
      `${environment.apiHost}/subjects/${subjectId}/room-templates`,
      template
    ) as Observable<RoomTemplate>;
  }

  updateTemplate(template:RoomTemplate): Observable<RoomTemplate> {
    return this.http.post(
      `${environment.apiHost}/room-templates/${template.id}`,
      template
    ) as Observable<RoomTemplate>;
  }

  deleteTemplate(templateId: string): Observable<RoomTemplate> {
    return this.http.delete<RoomTemplate>(
      `${environment.apiHost}/room-templates/${templateId}`
    );
  }

  recalculateScores(roomId: string, dryRun: boolean): Observable<Attendee[]> {
    return this.http.post<Attendee[]>(
      `${environment.apiHost}/rooms/${roomId}/recalculate-scores`,
      {},
      {params: {dryRun}}
    )
  }
}
